/**
 * MarkeeMix Studio — YouTube Operations CRM hub.
 *
 * Shows a grid of connected channels (owned + shared), a "Connect new channel"
 * CTA, and stat tiles per channel. Anonymous users see a polished sign-in
 * gate.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Youtube, Plus, Users, Settings, BarChart3, RefreshCw, ChevronRight,
  Sparkles, Loader2, AlertTriangle, LogIn, Bell, Calendar as CalIcon,
  Trophy, Trash, Search, CheckCircle2, FileText, Video, Image as ImageIcon,
  Hash, ScrollText, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SignInRequiredCard from "../tools/SignInRequiredCard";
import NotifBell from "./NotifBell";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

function fmtNumber(n) {
  if (n == null) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}

export default function StudioHub() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [boot, setBoot] = useState(null);
  const [channels, setChannels] = useState({ owned: [], shared: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async () => {
    try {
      const b = await axios.get(`${API}/api/studio/bootstrap`, { withCredentials: true });
      setBoot(b.data);
      if (!b.data?.authenticated) {
        setLoading(false);
        return;
      }
      const c = await axios.get(`${API}/api/studio/channels`, { withCredentials: true });
      setChannels(c.data || { owned: [], shared: [] });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  // Surface OAuth callback feedback.
  useEffect(() => {
    const ok = params.get("connected");
    const err = params.get("connect_error");
    if (ok) toast.success(`${ok} channel(s) connected to Studio`);
    if (err) toast.error(`Channel connect failed: ${err}`);
  }, [params]);

  const onConnect = () => {
    window.location.href = `${API}/api/studio/youtube/connect`;
  };

  const allChannels = [...(channels.owned || []), ...(channels.shared || [])];

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <span className="studio-eyebrow">
              <Sparkles size={12}/> MarkeeMix Studio · YouTube Operations
            </span>
            <h1 className="studio-h1">
              Run every channel <span className="studio-h1-accent">from one workspace.</span>
            </h1>
            <p className="studio-sub">
              Connect unlimited YouTube channels, plan content, brief your team, generate scripts +
              SEO + thumbnails, and ship videos — all from one dashboard.
            </p>
          </motion.div>

          {boot?.authenticated && (
            <div className="studio-hero-cta">
              <button className="studio-btn-primary" onClick={onConnect} data-testid="studio-connect-cta">
                <Youtube size={16}/> Connect a YouTube channel
              </button>
              <Link className="studio-btn-ghost" to="/studio/my-tasks" data-testid="studio-my-tasks">
                My tasks <ChevronRight size={14}/>
              </Link>
              <Link className="studio-btn-ghost" to="/studio/settings" data-testid="studio-settings-link">
                <Settings size={14}/> Settings
              </Link>
              <NotifBell />
            </div>
          )}
        </div>

        {loading && (
          <div className="studio-card studio-loading">
            <Loader2 size={22} className="ac-spin" style={{ color: "var(--ac-purple, #a78bfa)" }}/>
            <p>Loading your workspace…</p>
          </div>
        )}

        {!loading && error && (
          <div className="ac-error"><AlertTriangle size={16}/> {error}</div>
        )}

        {!loading && boot && boot.authenticated === false && (
          <SignInRequiredCard
            icon={<Youtube size={26} strokeWidth={1.6}/>}
            eyebrow="Studio · YouTube CRM"
            title="Sign in to set up your YouTube workspace"
            description="Studio connects your YouTube channels, organises your team, and runs the entire production pipeline from idea to publish."
            benefits={[
              "Unlimited YouTube channels",
              "Unified My-Tasks dashboard for editors / designers / SMM",
              "AI script + SEO + thumbnail brief generation",
            ]}
            testId="studio-signin-required"
          />
        )}

        {!loading && boot?.authenticated && allChannels.length === 0 && (
          <motion.div className="studio-empty"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            data-testid="studio-empty">
            <div className="studio-empty-icon"><Youtube size={36} /></div>
            <h2>No channels connected yet</h2>
            <p>Connect your first YouTube channel to start running your production pipeline.</p>
            <button className="studio-btn-primary studio-btn-xl" onClick={onConnect} data-testid="studio-empty-connect">
              <Plus size={16}/> Connect a YouTube channel
            </button>
            <p className="studio-empty-fineprint">
              You&apos;ll be asked to authorise MarkeeMix Studio in Google. We request the scopes needed
              to read channel stats, manage video metadata, publish, and read YouTube Analytics.
            </p>
          </motion.div>
        )}

        {!loading && boot?.authenticated && allChannels.length > 0 && (
          <>
            <OpsRow />
            <SetupChecklistRow channels={allChannels} />
            <div className="studio-section-h">
              <h2>Your channels</h2>
              <button className="studio-btn-ghost" onClick={onConnect} data-testid="studio-add-channel">
                <Plus size={14}/> Add channel
              </button>
            </div>
            <div className="studio-grid">
              {allChannels.map((c) => (
                <ChannelCard key={c.channel_id} ch={c} navigate={navigate} />
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </>
  );
}

function ChannelCard({ ch, navigate }) {
  const s = ch.stats || {};
  return (
    <motion.div
      className="studio-channel-card"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      onClick={() => navigate(`/studio/channels/${ch.channel_id}`)}
      data-testid={`studio-channel-${ch.channel_id}`}
    >
      <div className="studio-channel-head">
        {ch.thumbnail_url ? (
          <img src={ch.thumbnail_url} alt="" className="studio-channel-avatar" />
        ) : (
          <div className="studio-channel-avatar studio-channel-avatar--ph"><Youtube size={20}/></div>
        )}
        <div className="studio-channel-meta">
          <h3>{ch.title}</h3>
          <p>{ch.handle || `id: ${ch.channel_id.slice(0, 14)}…`}</p>
        </div>
        <span className={`studio-role studio-role--${ch.my_role || "creator"}`}>{ch.my_role || "member"}</span>
      </div>
      <div className="studio-channel-stats">
        <Stat icon={<Users size={14} />} label="Subscribers" value={fmtNumber(s.subscribers)} />
        <Stat icon={<BarChart3 size={14} />} label="Total views" value={fmtNumber(s.views)} />
        <Stat icon={<Youtube size={14} />} label="Videos" value={fmtNumber(s.videos)} />
      </div>
      <div className="studio-channel-foot">
        <span className="studio-pill">{ch.config?.niche || "No niche set"}</span>
        <span className="studio-go">Open <ChevronRight size={14}/></span>
      </div>
    </motion.div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="studio-stat">
      <span className="studio-stat-icon">{icon}</span>
      <div>
        <strong>{value}</strong>
        <small>{label}</small>
      </div>
    </div>
  );
}


function OpsRow() {
  const [alerts, setAlerts] = useState(null);
  const [digest, setDigest] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [a, d] = await Promise.all([
          axios.get(`${API}/api/studio/alerts`, { withCredentials: true }),
          axios.get(`${API}/api/studio/digest/daily`, { withCredentials: true }),
        ]);
        if (alive) { setAlerts(a.data); setDigest(d.data); }
      } catch (_) { /* silent */ }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <div className="studio-ops-row" data-testid="studio-ops-row">
      <div className="studio-ops-card studio-ops-card--alerts" data-testid="studio-alerts">
        <header>
          <span className="studio-eyebrow"><Bell size={12}/> Live alerts</span>
          {alerts?.summary && (
            <div className="studio-ops-badges">
              <span className="studio-pill studio-pill--bad">{alerts.summary.critical} critical</span>
              <span className="studio-pill studio-pill--warn">{alerts.summary.warning} warn</span>
              <span className="studio-pill studio-pill--sm">{alerts.summary.info} info</span>
            </div>
          )}
        </header>
        {!alerts ? (
          <p className="studio-muted">Scanning…</p>
        ) : (alerts.alerts || []).length === 0 ? (
          <p className="studio-muted">All clear — nothing needs your attention right now. 🎯</p>
        ) : (
          <ul className="studio-ops-list">
            {(alerts.alerts || []).slice(0, 6).map((a, i) => (
              <li key={i} className={`studio-ops-li studio-ops-li--${a.severity}`} data-testid={`alert-${i}`}>
                <strong>{a.title}</strong>
                <span>{a.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="studio-ops-card" data-testid="studio-digest">
        <header>
          <span className="studio-eyebrow"><CalIcon size={12}/> Today&apos;s digest</span>
          <small className="studio-muted">{digest?.channel_count || 0} channel(s)</small>
        </header>
        {!digest ? (
          <p className="studio-muted">Loading…</p>
        ) : (
          <div className="studio-digest-grid">
            <DigestStat label="Due today" n={digest.sections?.due_today?.length || 0}/>
            <DigestStat label="Recording" n={digest.sections?.recording_today?.length || 0}/>
            <DigestStat label="Editing now" n={digest.sections?.editing_now?.length || 0}/>
            <DigestStat label="Scheduled" n={digest.sections?.scheduled_today?.length || 0}/>
            <DigestStat label="Pending approval" n={digest.sections?.pending_approval?.length || 0}/>
          </div>
        )}
      </div>

      <div className="studio-ops-card studio-ops-nav" data-testid="studio-quicknav">
        <header><span className="studio-eyebrow"><Sparkles size={12}/> Workspace tools</span></header>
        <Link to="/studio/topic-suggestions" className="studio-quick" data-testid="nav-topic-suggestions">
          <Wand2 size={14}/> AI Topic Suggestions <ChevronRight size={12}/>
        </Link>
        <Link to="/studio/reports" className="studio-quick" data-testid="nav-reports">
          <FileText size={14}/> Reports &amp; PDF export <ChevronRight size={12}/>
        </Link>
        <Link to="/studio/approvals" className="studio-quick" data-testid="nav-approvals">
          <CheckCircle2 size={14}/> Approvals inbox <ChevronRight size={12}/>
        </Link>
        <Link to="/studio/calendar" className="studio-quick" data-testid="nav-calendar">
          <CalIcon size={14}/> Cross-channel calendar <ChevronRight size={12}/>
        </Link>
        <Link to="/studio/search" className="studio-quick" data-testid="nav-search">
          <Search size={14}/> Global content search <ChevronRight size={12}/>
        </Link>
        <Link to="/studio/scorecard" className="studio-quick" data-testid="nav-scorecard">
          <Trophy size={14}/> Team scorecard <ChevronRight size={12}/>
        </Link>
        <Link to="/studio/trash" className="studio-quick" data-testid="nav-trash">
          <Trash size={14}/> Trash &amp; restore <ChevronRight size={12}/>
        </Link>
        <Link to="/studio/workload" className="studio-quick" data-testid="nav-workload">
          <Users size={14}/> Workload balancer <ChevronRight size={12}/>
        </Link>
      </div>
      <div className="studio-ops-card studio-ops-nav" data-testid="studio-role-nav">
        <header><span className="studio-eyebrow"><Users size={12}/> Role dashboards</span></header>
        <Link to="/studio/role/editor" className="studio-quick" data-testid="nav-role-editor">
          <Video size={14}/> Editor dashboard <ChevronRight size={12}/>
        </Link>
        <Link to="/studio/role/designer" className="studio-quick" data-testid="nav-role-designer">
          <ImageIcon size={14}/> Designer dashboard <ChevronRight size={12}/>
        </Link>
        <Link to="/studio/role/smm" className="studio-quick" data-testid="nav-role-smm">
          <Hash size={14}/> SMM dashboard <ChevronRight size={12}/>
        </Link>
        <Link to="/studio/role/creator" className="studio-quick" data-testid="nav-role-creator">
          <ScrollText size={14}/> Creator dashboard <ChevronRight size={12}/>
        </Link>
      </div>
    </div>
  );
}

function DigestStat({ label, n }) {
  return (
    <div className="studio-digest-stat" data-testid={`digest-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <strong>{n}</strong>
      <small>{label}</small>
    </div>
  );
}

function SetupChecklistRow({ channels }) {
  const primary = channels[0];
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!primary) return;
    let alive = true;
    axios.get(`${API}/api/studio/channels/${primary.channel_id}/setup-status`, { withCredentials: true })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [primary?.channel_id]);

  if (!data) return null;
  const pct = data.percent || 0;
  return (
    <div className="studio-setup-card" data-testid="studio-setup-checklist">
      <header>
        <div>
          <span className="studio-eyebrow">🚀 CRM setup</span>
          <h3>Your YouTube CRM setup is <strong>{pct}% complete</strong></h3>
          <small className="studio-muted">Showing for: <Link to={`/studio/channels/${primary.channel_id}`} className="studio-link">{primary.title}</Link></small>
        </div>
        <div className="studio-setup-pctring">
          <div className="studio-setup-bar"><div style={{ width: `${pct}%` }}/></div>
          <small>{data.completed}/{data.total} done</small>
        </div>
      </header>
      <ul className="studio-setup-list" data-testid="setup-list">
        {data.items.map((it) => (
          <li key={it.key} className={it.done ? "studio-setup-done" : (it.required ? "studio-setup-req" : "")} data-testid={`setup-${it.key}`}>
            <span>{it.done ? "✅" : (it.required ? "🔴" : "⚪")}</span>
            <span>{it.label}{it.detail ? ` · ${it.detail}` : ""}</span>
            {!it.done && (
              <Link to={`/studio/channels/${primary.channel_id}`} className="studio-link studio-link--ai" style={{ marginLeft: "auto" }}>
                Fix →
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

